import { useGame } from '@/app/providers/GameContextProvider';
import { GameStep } from '@/lib/types/common/GameStep';
import React, { useState } from 'react';
import LobbyPanel from './LobbyPanel';
import { AnimatePresence } from 'framer-motion';
import { useTranslation } from '@/hooks/useTranslation';
import AvatarSelector from './AvatarSelector';
import { Avatar } from '@/lib/types/common/Avatar';
import { setStoredAvatarId } from '@/lib/utils/avatarUtils';
import { PlayerRow } from './shared';
import { useGlobalPopup } from '@/app/providers/GlobalPopupProvider';
import { AUDIO, useAudio } from '@/app/providers/AudioProvider';

const PlayersList = () => {
    const { gameState, room } = useGame();
    const { t } = useTranslation();
    const [showAvatarSelector, setShowAvatarSelector] = useState(false);
    const { showPopup } = useGlobalPopup();
    const { play } = useAudio();

    const handleAvatarSelect = async (avatar: Avatar) => {
        if (!room) return;
        try {
            setStoredAvatarId(avatar.id);
            room.send("updatePlayerAvatar", { avatarId: avatar.id });
            showPopup({ type: 'success', message: t("lobby.avatarUpdated") || "Avatar updated!" });
        } catch {
            showPopup({ type: 'error', message: t("lobby.failedToUpdateAvatar") || "Failed to update avatar" });
        }
    };

    if (!gameState || !room) return null;

    const playersArray = Array.from(gameState.players?.entries() || []);
    const currentPlayer = gameState.players?.get(room.sessionId);
    const isInLobby = (currentPlayer?.inLobby && gameState.gameStep === GameStep.RECAP) || gameState.gameStep === GameStep.LOBBY;

    return (
        <LobbyPanel name={t("lobby.party")} className="rounded w-full md:w-fit">
            <div className="flex flex-col gap-2 md:gap-4 min-w-40">
                <AnimatePresence mode="popLayout">
                    {playersArray.map(([sessionId, player]) => (
                        <PlayerRow
                            key={sessionId}
                            sessionId={sessionId}
                            player={player}
                            isCurrentPlayer={sessionId === room.sessionId}
                            isInLobby={isInLobby}
                            gameStep={gameState.gameStep}
                            gameMasterId={gameState.gameMasterId}
                            currentRound={gameState.currentRound}
                            currentHazards={Array.from(gameState.currentHazards || [])}
                            onAvatarClick={() => (play(AUDIO.CLICK), setShowAvatarSelector(true))}
                            showScoreBar={true}
                            totalScore={player.totalScore}
                            currentStreak={player.currentStreak}
                        />
                    ))}
                </AnimatePresence>
            </div>

            {/* Avatar Selector Modal */}
            <AvatarSelector
                isOpen={showAvatarSelector}
                currentAvatarId={gameState?.players?.get(room?.sessionId || '')?.avatarId}
                onSelect={handleAvatarSelect}
                onClose={() => setShowAvatarSelector(false)}
            />
        </LobbyPanel>
    );
};

export default PlayersList;